import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * Hook to get an authenticated video blob URL for <video> elements.
 * Fetches video binary data via axios, creates a local blob URL.
 * This bypasses all proxy/CDN/CORS issues with streaming endpoints.
 */
export function useVideoUrl(projectId, type = "proxy", enabled = true) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!projectId || !enabled) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setVideoUrl(null);
      return;
    }

    let cancelled = false;

    const fetchBlob = async () => {
      setLoading(true);
      setError(null);
      try {
        const tokenRes = await axios.get(
          `${API}/projects/${projectId}/video/token`,
          { params: { type } }
        );
        if (cancelled) return;

        const playUrl = `${API}/projects/${projectId}/video/play?token=${tokenRes.data.token}`;
        const videoRes = await axios.get(playUrl, { responseType: "blob" });
        if (cancelled) return;

        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);

        const blob = new Blob([videoRes.data], { type: "video/mp4" });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setVideoUrl(url);
      } catch (err) {
        if (!cancelled) {
          console.warn("Video blob fetch failed:", err?.message);
          setError(err);
          setVideoUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBlob();

    return () => {
      cancelled = true;
    };
  }, [projectId, type, enabled, version]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const refresh = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setVideoUrl(null);
    setVersion((v) => v + 1);
  };

  return { videoUrl, loading, error, refresh };
}
